import {CSSProperties, CSSPropertiesComplete} from 'aphrodite';

import * as Colors from 'ui-library/Colors';

export const ellipsisText: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const multiLineEllipsisText = (numLineClamp: number): CSSProperties => ({
  overflow: 'hidden',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
  textOverflow: 'ellipsis',
  // CSS has no (clean) pure solution for multi-line ellipses for text overflow
  // The following is the simplest implementation, and has modest support across browsers
  // Chrome 6, Firefox 68, Safari 5 (similar for mobile)
  // See for details:
  //   - https://css-tricks.com/line-clampin/ ("The Standardized Way")
  //   - https://developer.mozilla.org/en-US/docs/Web/CSS/-webkit-line-clamp#browser_compatibility
  WebkitLineClamp: numLineClamp,
  WebkitBoxOrient: 'vertical',
  display: '-webkit-box' as CSSPropertiesComplete['display'],
});

// Useful when you want the first child to be on the left and the second child on the right
export const lrSplit: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
};

export const miniSpacingBottom: CSSProperties = {
  marginBottom: '4px',
};

export const miniSpacingLeft: CSSProperties = {
  marginLeft: '4px',
};

export const miniSpacingRight: CSSProperties = {
  marginRight: '4px',
};

export const miniSpacingTop: CSSProperties = {
  marginTop: '4px',
};

export const spacingBottom: Pick<CSSProperties, 'marginBottom'> = {
  marginBottom: '20px',
};

export const moderateSpacingBottom: CSSProperties = {
  marginBottom: '15px',
};

export const smallSpacingTop: CSSProperties = {
  marginTop: '10px',
};

export const smallSpacingBottom: CSSProperties = {
  marginBottom: '10px',
};

export const smallSpacingLeft: CSSProperties = {
  marginLeft: '10px',
};

export const smallSpacingRight: CSSProperties = {
  marginRight: '10px',
};

export const noSpacingBottom: Pick<CSSProperties, 'marginBottom'> = {
  marginBottom: '0',
};

export const disabled: CSSProperties = {
  color: Colors.GRAY_DARK,
  cursor: 'default',
};

export const faded: CSSProperties = {
  color: Colors.GRAY_DARK,
};

export const solidLightGrayBorder: CSSProperties = {
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: Colors.GRAY_LIGHT,
  borderRadius: '4px',
};

/**
 * Useful for making sure styles from `less` files don't effect the element.
 *
 * Note: This should be used sparingly.
 * Callers should expect that the list of properties reset will grow over time.
 * That said, when adding new styles to this list, try to make sure you're not breaking existing usages which
 * may have a hard time enumerating all possible styles ahead of time.
 *
 * @example
 * <input type="checkbox" className={css(styles.checkbox)} />
 * const styles = StyleSheet.create({
 *  checkbox: {
 *    ...CSS_RESET,
 *    width: 14,
 *    ...
 *   }
 * })
 */
export const CSS_RESET: CSSProperties = {
  margin: 0,
  padding: 0,
  cursor: 'unset',
};

// This padding style was copied from .simpleDivTable-header and should be kept in sync with it
export const padded: CSSProperties = {
  padding: '12px 14px',
};

// Copied from the .img-retina less mixin
export const RETINA_MEDIA_QUERY = `\
only screen and (-webkit-min-device-pixel-ratio: 2),\
only screen and (min--moz-device-pixel-ratio: 2),\
only screen and (-o-min-device-pixel-ratio: 2/1),\
only screen and (min-device-pixel-ratio: 2),\
only screen and (min-resolution: 192dpi),\
only screen and (min-resolution: 2dppx)\
`;

// Wrap the innerStyles in a media query to detect "retina" (high res) displays
export const retinaQueryWrapper = (innerStyles: CSSProperties): CSSProperties => ({
  [`@media ${RETINA_MEDIA_QUERY}`]: innerStyles,
});

export const SemanticColors = {
  dangerColor: Colors.RED_LIGHT,
  warningColor: Colors.ORANGE_LIGHT,
  darkWarningColor: Colors.ORANGE,
  successColor: Colors.GREEN,
  primaryColor: Colors.BLUE_LIGHT,
};
